export class ClientDateHandler {
  dateValue: Date | undefined;
  dateIsNull: boolean;
  isUTC: boolean;

  constructor() {
    this.dateValue = undefined;
    this.dateIsNull = false;
    this.isUTC = false;
  }

  resetDate = (): void => {
    this.dateValue = undefined;
  };

  setDateValue = (isUTC: boolean, dateValue?: Date | undefined): void => {
    this.dateValue = dateValue;
    this.isUTC = isUTC;
  };

  getLocalDate = (): Date | undefined => {
    const date = this.getDate(false);
    return date;
  };

  getUTCDate = (): Date | undefined => {
    const date = this.getDate(true);
    return date;
  };

  getDate = (isUtc: boolean): Date | undefined => {
    if (this.dateIsNull) return undefined;

    if (typeof this.dateValue == 'object') {
      const years = this.getYear(isUtc);
      const month = this.getMonth(isUtc) - 1;
      const day = this.getDay(isUtc);
      const hours = this.getHours(isUtc);
      const minutes = this.getMinutes(isUtc);
      const seconds = this.getSeconds(isUtc);

      const date = new Date(years, month, day, hours, minutes, seconds);

      return date;
    }

    return undefined;
  };

  getJSON = (): string => {
    //POSSIBLE PROBLEM: Not setting utc here just getting it here for formatDate
    const dateObject = {
      value: this.formatDate(this.getUTC()),
      utc: this.getUTC() ? '1' : '0'
    };

    const json = JSON.stringify(dateObject);
    return json;
  };

  formatDate = (isUtc: boolean): string => {
    if (this.dateValue == null) return '!null';

    const years = this.getYear(isUtc);
    const month = this.getMonth(isUtc);
    const day = this.getDay(isUtc);
    const hours = this.getHours(isUtc);
    const minutes = this.getMinutes(isUtc);
    const seconds = this.getSeconds(isUtc);

    const isInvalidDate = isNaN(day) || isNaN(month) || isNaN(years);
    if (isInvalidDate) return '!invalid';

    const dateText = `${years}-${this.leftPad(month)}-${this.leftPad(day)}`;
    const timeText = `T${this.leftPad(hours)}:${this.leftPad(
      minutes
    )}:${this.leftPad(seconds)}`;

    const formattedText = dateText + timeText;
    return formattedText;
  };

  parseJSON = (dateJson: string): void => {
    if (dateJson == null) {
      this.resetDate();
      this.dateIsNull = true;
      return;
    }

    const jsonDate = JSON.parse(dateJson);
    const isValid = jsonDate.value != null && jsonDate.utc != null;
    if (isValid) {
      if (jsonDate.value == '!null') {
        this.resetDate();
        this.dateIsNull = true;
      } else {
        const dateTimeParts: string[] = jsonDate.value.split('T');

        const dateText = dateTimeParts[0];
        const dateValues = this.parseDateValues(dateText);
        const timeText = dateTimeParts[1];
        const timeValues = this.parseTimeValues(timeText);

        this.resetDate();
        this.setUTC(jsonDate.utc == '1');

        this.dateValue = new Date();

        if (dateValues != null) {
          this.setYear(dateValues.year);
          this.setMonth(dateValues.month);
          this.setDay(dateValues.day);
        }

        if (timeValues != null) {
          this.setHours(timeValues.hours);
          this.setMinutes(timeValues.minutes);
          this.setSeconds(timeValues.seconds);
        }
      }
    }
  };

  //POSSIBLE ERROR: Removed NAN checks as it viewed them as strings not numbers
  parseDateValues = (
    dateText: string
  ): { year: number; month: number; day: number } => {
    const [yyyy, mm, dd] = dateText.split('-');
    return {
      year: parseInt(yyyy, 10),
      month: parseInt(mm, 10),
      day: parseInt(dd, 10)
    };
  };

  parseTimeValues = (
    timeText: string
  ): { hours: number; minutes: number; seconds: number } => {
    const [hh, mm, ss] = timeText.split(':');
    return {
      hours: parseInt(hh, 10),
      minutes: parseInt(mm, 10),
      seconds: parseInt(ss, 10)
    };
  };

  setUTC = (isUTC: boolean): void => {
    this.isUTC = isUTC;
  };

  getUTC = (): boolean => {
    return this.isUTC;
  };

  getUTCValue = (isUtc: boolean | undefined): boolean => {
    const utcValue = isUtc != undefined ? isUtc : this.isUTC;
    return utcValue;
  };

  getYear = (isUtc?: boolean): number => {
    if (this.dateIsNull) return new Date().getFullYear();
    let year = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) year = this.dateValue.getUTCFullYear();
      else year = this.dateValue.getFullYear();
    }

    return year;
  };

  setYear = (year: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCFullYear(year);
      else this.dateValue.setFullYear(year);
    }
  };

  getMonth = (isUtc?: boolean): number => {
    if (this.dateIsNull) return new Date().getMonth();
    let month = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) month = this.dateValue.getUTCMonth();
      else month = this.dateValue.getMonth();
    }

    const adjustedMonth = month + 1;
    return adjustedMonth;
  };

  setMonth = (month: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      const adjustedMonth = month - 1;
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCMonth(adjustedMonth);
      else this.dateValue.setMonth(adjustedMonth);
    }
  };

  getDay = (isUtc?: boolean): number => {
    if (this.dateIsNull) return new Date().getDay();
    let day = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) day = this.dateValue.getUTCDate();
      else day = this.dateValue.getDate();
    }

    return day;
  };

  setDay = (day: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCDate(day);
      else this.dateValue.setDate(day);
    }
  };

  getHours = (isUtc?: boolean): number => {
    if (this.dateIsNull) return 0;
    let hours = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) hours = this.dateValue.getUTCHours();
      else hours = this.dateValue.getHours();
    }

    return hours;
  };

  setHours = (hours: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCHours(hours);
      else this.dateValue.setHours(hours);
    }
  };

  getMinutes = (isUtc?: boolean): number => {
    if (this.dateIsNull) return 0;
    let minutes = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) minutes = this.dateValue.getUTCMinutes();
      else minutes = this.dateValue.getMinutes();
    }

    return minutes;
  };

  setMinutes = (minutes: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCMinutes(minutes);
      else this.dateValue.setMinutes(minutes);
    }
  };

  getSeconds = (isUtc?: boolean): number => {
    if (this.dateIsNull) return 0;
    let seconds = -1;

    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) seconds = this.dateValue.getUTCSeconds();
      else seconds = this.dateValue.getSeconds();
    }

    return seconds;
  };

  setSeconds = (seconds: number, isUtc?: boolean): void => {
    if (this.dateValue !== undefined) {
      if (this.getUTCValue(isUtc)) this.dateValue.setUTCSeconds(seconds);
      else this.dateValue.setSeconds(seconds);
    }
  };

  leftPad = (value: number): string => {
    if (value < 10) return '0' + value;
    else return value.toString();
  };
}

export default ClientDateHandler;
